import React, { FC, useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'
import Link from 'next/link'
import { FlexColumn } from '@/atoms/FlexContainers'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { CopyIcon } from '@/atoms/Icons/CopyIcon'
import { FacebookCircleIcon } from '@/atoms/Icons/FacebookCircleIcon'
import { LinkedInLettersIcon } from '@/atoms/Icons/LinkedInLettersIcon'
import { TwitterXIcon } from '@/atoms/Icons/TwitterXIcon'
import { Image } from '@/atoms/Image'
import { AsH1 } from '@/atoms/Text'
import { IconColor } from '@/atoms/utils'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { Modal } from '@/molecules/Modal'
import { useToast } from '@/molecules/Toast'
import { useUser } from '@/services/UserService'
import { SweepstakeObject } from '@/types/codegen-federation'
import { useLoginUrl, useSignupUrl } from '@/utils/auth-utils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import {
  GFooterMD,
  GParagraphLG,
  GParagraphMD,
  GParagraphSM,
  GResponsiveText,
  GTitleXL,
  GTitleXS,
  GTitleXXL,
} from '@/views/PreSales/PreSalesGiveawayView/components/UI/Typography'
import { useGiveawayContext } from '@/views/PreSales/PreSalesGiveawayView/context/GiveawayContext'
import { generateShareUrl } from '../../../services/ShareUrlService'

interface ShareProps {
  isOpen: boolean
  onClose: () => void
  imageUrl: string
  imageUrlMobile: string
  shareText: string
  rulesLink: SweepstakeObject['rulesUrl']
}
interface SocialShare {
  name: string
  icon: JSX.Element
  href?: string
  onClick?: () => void
  color2?: IconColor
}

export const ShareGiveawayModal: FC<ShareProps> = ({
  isOpen,
  onClose,
  imageUrl,
  imageUrlMobile,
  shareText,
  rulesLink,
}) => {
  const router = useSafeRouter()
  const { signupUrl, trackSignupStarted } = useSignupUrl(router.asPath)
  const { loginUrl, trackLoginStarted } = useLoginUrl(router.asPath)
  const { t } = useTranslate('tickets')
  const { user } = useUser()
  const [shareUrl, setShareUrl] = useState('')
  const { theatricalSlug } = useGiveawayContext()

  useEffect(() => {
    const defaultUrl = `${paths.getBaseURL()}${paths.preSales.giveaway(
      theatricalSlug,
    )}?project=${theatricalSlug}&product=presales`
    setShareUrl(defaultUrl)

    if (user?.id) {
      generateShareUrl({
        baseUrl: `https://www.angel.com${paths.preSales.giveaway(theatricalSlug)}`,
        userId: user.id,
        slug: theatricalSlug,
        aliasPrefix: 'hm-',
        utmParams: {
          source: 'user_referral',
          medium: 'giveaway_funnel_web',
          campaign: `${theatricalSlug}_giveaway`,
        },
      }).then((generatedUrl) => {
        if (generatedUrl) setShareUrl(generatedUrl)
      })
    }
  }, [theatricalSlug, user?.id])

  const encodedUrl = encodeURIComponent(shareUrl)
  const text = `${shareText} #AngelStudios\n\n ${shareUrl}`
  const encodedShareText = encodeURIComponent(shareText)

  const shareIcons: SocialShare[] = [
    {
      name: 'Facebook',
      icon: (
        <FacebookCircleIcon
          size={24}
          color1={'#fff' as IconColor}
          color2={
            theatricalSlug === slugs.soloMio
              ? ('giveaway-primary' as IconColor)
              : ('giveaway-primary-accent' as IconColor)
          }
        />
      ),
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
    {
      name: 'LinkedIn',
      icon: <LinkedInLettersIcon size={24} color={'#fff' as IconColor} />,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    },
    {
      name: 'Twitter',
      icon: <TwitterXIcon size={24} color={'#fff' as IconColor} />,
      href: `https://twitter.com/intent/tweet?text=${encodedShareText}`,
    },
  ]

  const linkCopied = t('linkCopied', 'Link copied.')
  const toast = useToast()

  const handleCopyClick = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      toast.showToast(linkCopied, { icon: <CopyIcon size={24} color="core-gray-200" /> })
    })
  }, [toast, text, linkCopied])

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="mx-auto"
      childWrapperClassName="!m-0 !p-0"
      panelClasses="!p-0 !w-full rounded-3xl overflow-hidden !max-w-[361px] md:!max-w-[782px] bg-[#E1E1E1]"
      data-slug={theatricalSlug}
    >
      <div className="relative flex flex-col md:flex-row">
        <div
          className="absolute right-4 top-4 z-10 hidden cursor-pointer rounded-full bg-core-gray-300 p-2 backdrop-blur-xl  md:block "
          onClick={onClose}
        >
          <CloseIcon color="giveaway-background-primary-dark" className="opacity-60" size={18} />
        </div>
        <div
          className="absolute right-4 top-4 z-10 cursor-pointer rounded-full bg-white/20 p-2 backdrop-blur-xl  md:hidden"
          onClick={onClose}
        >
          <CloseIcon color="white" className="opacity-60" size={18} />
        </div>
        <div className="relative hidden h-[578px] w-full max-w-[390px] overflow-hidden bg-[var(--giveaway-background-subtle-light)] md:block">
          <Image priority src={imageUrl} alt="" fill className="object-cover" />
        </div>
        <div className="relative h-[200px] w-full overflow-hidden bg-[var(--giveaway-background-subtle-light)] md:hidden">
          <Image priority src={imageUrlMobile} height={200} width={361} alt="" className="h-full object-cover" />
        </div>
        <div className="relative flex w-full flex-col items-center justify-center p-6 pt-8 md:p-8 md:pt-12">
          <FlexColumn className="gap-3 text-center md:gap-4">
            <FlexColumn className="gap-1">
              <GResponsiveText
                className="text-center uppercase text-[var(--giveaway-primary-dark)] md:max-w-[540px]"
                as={AsH1}
                MobileTextComponent={GTitleXL}
                DesktopTextComponent={theatricalSlug === slugs.sketch ? GTitleXXL : GTitleXL}
              >
                {t('shareEarn', 'SHARE AND EARN')}
              </GResponsiveText>
              <GResponsiveText
                className="text-center text-[var(--giveaway-text-subtle-light)] md:max-w-[540px]"
                MobileTextComponent={GTitleXS}
                DesktopTextComponent={GTitleXS}
              >
                {t('earnEntries', 'Earn 100 Extra Entries')}
              </GResponsiveText>
            </FlexColumn>

            <GResponsiveText
              className="text-center text-[var(--giveaway-text-subtle-light)] md:max-w-[540px]"
              MobileTextComponent={GParagraphLG}
              DesktopTextComponent={GParagraphLG}
            >
              {t(
                'inviteToWin',
                "Invite your friends and family to check out this offer. If they pre-order tickets, you'll get 100 extra entries to win.",
              )}
            </GResponsiveText>
            <GResponsiveText
              className="text-center text-[var(--giveaway-text-subtle-light)] md:max-w-[540px]"
              MobileTextComponent={GFooterMD}
              DesktopTextComponent={GFooterMD}
            >
              <Translate t={t} i18nKey="shareGiveawayTerms">
                By sharing this promotion, you agree to be bound by the terms of the{' '}
                <Link href={rulesLink ?? ''} target="_blank" className="underline">
                  Official Rules
                </Link>{' '}
                and{' '}
                <Link href={paths.legal.privacyPolicy} target="_blank" className="underline">
                  Privacy Policy
                </Link>
                .
              </Translate>
            </GResponsiveText>

            {!user && (
              <GResponsiveText
                className="mx-2 rounded-lg border-[1.5px] border-[var(--giveaway-primary-subtle)] bg-[var(--giveaway-primary-subtle-light)] px-2 py-1 text-[var(--giveaway-subtle)]"
                MobileTextComponent={GParagraphSM}
                DesktopTextComponent={GParagraphMD}
              >
                <Translate t={t} i18nKey="giveawayLoginShare">
                  <a href={loginUrl} onClick={() => trackLoginStarted('PreSalesGiveaway')} className="underline">
                    Login
                  </a>{' '}
                  or{' '}
                  <a href={signupUrl} onClick={() => trackSignupStarted('PreSalesGiveaway')} className="underline">
                    create an account
                  </a>{' '}
                  to participate in this promotion.
                </Translate>
              </GResponsiveText>
            )}
          </FlexColumn>

          {!!user && (
            <FlexColumn className="mt-6 w-full gap-6 md:mt-8 md:gap-4">
              <div className="flex justify-center space-x-4">
                {shareIcons.map(({ name, icon, href }) => (
                  <a
                    key={name}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classNames(
                      'px-3 py-3.5 transition-colors duration-200 hover:opacity-80 bg-giveaway-primary-accent',
                      theatricalSlug === slugs.sketch ? '' : ' rounded-xl',
                      theatricalSlug === slugs.soloMio && '!bg-giveaway-primary',
                    )}
                  >
                    {icon}
                  </a>
                ))}
              </div>
              <div className="flex w-full items-center bg-core-gray-400 p-2">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="grow truncate bg-transparent pl-1 text-sm text-giveaway-subtle outline-none"
                />
                <button
                  onClick={handleCopyClick}
                  className="whitespace-nowrap  rounded-xl bg-giveaway-secondary px-[20px] py-[11px] text-sm text-white"
                  aria-label="Copy link"
                >
                  {t('copyShareLinkUrl', 'COPY URL')}
                </button>
              </div>
            </FlexColumn>
          )}
        </div>
      </div>
    </Modal>
  )
}
